// @ts-nocheck
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Typography,
  Grid,
  FormHelperText,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useFormik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import {
  FCheckbox,
  FHAntdDateTimePicker,
  FBoxContainer,
  FHButton,
  FHInputField,
  FProgressBar,
  FHSelect,
  FHPageLink,
} from '../../components/base';
import { providerActions } from '../../store';
import { disabledFutureDate, phoneNumberFormatter, cleanNumericInputValue } from '../../helpers';
import { FORTA_BROWN, FORTA_ERROR } from '../../utils/colors';
import { trimsValuesInObject } from '../../utils/common';
import PageContainer from '../../components/layout/PageContainer';
import { BackgroundTypes, STANDARD_ERROR_MESSAGES } from '../../constants';
import {
  EmploymentTypeOptions,
  JobTitleOptions,
  ProviderTrainingStatusOptions,
  SupportedLanguages,
} from '../constants/general';
import { ProviderStatusEnum } from '../../pages/constants';

import type { RootState } from '../../store';
import type { Moment } from 'moment';
import type { SelectChangeEvent } from '@mui/material';
import type { ChangeEvent } from 'react';
import type { IOption, IProvider, SelectValueType } from '../../utils/types';

function AddProviderForm() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { loading: providerLoading, allProvidersList } = useSelector(
    (state: RootState) => state.providers,
  );

  const { required, invalidValue, tenDigits } = STANDARD_ERROR_MESSAGES;

  const ProviderValidationSchema = Yup.object().shape({
    first_name: Yup.string().trim().required(required),
    last_name: Yup.string().trim().required(required),
    email: Yup.string().trim().email(invalidValue).required(required),
    sex: Yup.string().trim().required(required),
    dob: Yup.string().trim().required(required),
    employment_type: Yup.string().optional().nullable(),
    status: Yup.string().required(required),
    job_title: Yup.string().optional().nullable(),
    phone: Yup.string().trim().min(12, tenDigits).max(12, tenDigits).required(required),
    state_license: Yup.string().optional(),
    caqh_id: Yup.string().optional(),
    payroll_id: Yup.string().optional(),
    date_certification_start: Yup.string().optional().nullable(),
    date_certification_end: Yup.string().optional().nullable(),
    street_address_line_one: Yup.string().trim().required(required),
    street_address_city: Yup.string().trim().required(required),
    street_address_state: Yup.string().trim().required(required),
    preferred_language: Yup.string().oneOf(Object.values(SupportedLanguages)).optional().nullable(),
    supported_languages: Yup.array()
      .of(Yup.string().oneOf(Object.values(SupportedLanguages)))
      .optional()
      .nullable(),
    street_address_zip: Yup.number()
      .typeError(invalidValue)
      .positive(invalidValue)
      .integer(invalidValue)
      .required(required),
  });

  const createProvider = async (values: any) => {
    const trimedValues = trimsValuesInObject(values);
    const phone = cleanNumericInputValue(trimedValues['phone']);
    try {
      const createProviderResponse = await dispatch(
        providerActions.createProvider({
          data: {
            ...trimedValues,
            phone,
          },
          getAccessTokenSilently,
        }),
      );
      if (createProviderResponse?.error) {
        toast.error(createProviderResponse.error);
      }
      return createProviderResponse?.payload;
    } catch {
      toast.error('An error occurred while creating the provider.');
      return null;
    }
  };

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      middle_name: '',
      email: '',
      phone: '',
      dob: null,
      employment_type: null,
      status: ProviderStatusEnum.NOT_TRAINING,
      job_title: null,
      date_certification_start: null,
      date_certification_end: null,
      caqh_id: '',
      payroll_id: '',
      sex: '',
      street_address_line_one: '',
      street_address_line_two: '',
      street_address_city: '',
      street_address_state: '',
      preferred_language: SupportedLanguages.English,
      supported_languages: [SupportedLanguages.English],
      street_address_zip: '',
      npi: '',
      certification: '',
      state_license: '',
    },
    onSubmit: async (values) => {
      //Create provider and get provider ID from Payload
      const provider = await createProvider(values);

      if (provider) {
        handleUpdateProviderReduxState(provider);
      }
    },
    validationSchema: ProviderValidationSchema,
  });
  const { values, setFieldValue, submitForm, errors, touched } = formik;

  const handleUpdateFieldValues = (key: string, value: any) => {
    let formattedValue = value;
    if (key === 'phone') {
      formattedValue = phoneNumberFormatter(cleanNumericInputValue(value));
    } else if (key === 'npi') {
      formattedValue = cleanNumericInputValue(value);
    }
    setFieldValue(key, formattedValue);
  };

  const handleSupportedLanguageChange = (language: SupportedLanguages, checked: boolean) => {
    const currentLanguages = values.supported_languages || [];
    if (checked) {
      // Add language if not already present
      if (!currentLanguages.includes(language)) {
        setFieldValue('supported_languages', [...currentLanguages, language]);
      }
    } else {
      // Remove language, but keep at least one language (English)
      if (currentLanguages.length > 1) {
        setFieldValue(
          'supported_languages',
          currentLanguages.filter((lang: SupportedLanguages) => lang !== language),
        );
        setFieldValue('preferred_language', SupportedLanguages.English);
      }
    }
  };

  if (providerLoading) {
    return <FProgressBar />;
  }

  const handleUpdateProviderReduxState = (payload: IProvider) => {
    if (!allProvidersList.length) return; // Do not update list if not fetched. It will be updated when user opens admin dashboard
    const updatedAllProviderList = [...allProvidersList, payload];
    dispatch(
      providerActions.updateState({
        key: 'allProvidersList',
        value: updatedAllProviderList,
      }),
    );
  };

  return (
    <PageContainer>
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer
          dataTestId="addProviderForm"
          sx={{ borderRadius: '20px', boxShadow: 'none' }}
        >
          <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
            <Grid container>
              <Grid item xs={12} md={9} lg={7}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box
                    ml={1}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexGrow: 1,
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography textAlign="left" variant="h4">
                      Add Provider
                    </Typography>
                    <Typography fontSize={14} textAlign="right" alignSelf={'flex-end'}>
                      <span style={{ color: 'red', fontSize: 16 }}>*</span>indicates mandatory
                      fields
                    </Typography>
                  </Box>
                  <Box
                    sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                  />
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        First Name<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerFirstName',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['first_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('first_name', e.target.value)
                        }
                        error={touched.first_name && Boolean(errors.first_name)}
                        helperText={touched.first_name && errors.first_name}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Last Name<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerLastName',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['last_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('last_name', e.target.value)
                        }
                        error={touched.last_name && Boolean(errors.last_name)}
                        helperText={touched.last_name && errors.last_name}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Middle Name:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerMiddleName',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['middle_name']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('middle_name', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>NPI:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerNPI',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['npi']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('npi', e.target.value)
                        }
                        error={touched.npi && Boolean(errors.npi)}
                        helperText={touched.npi && errors.npi}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Certification:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerCertification',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['certification']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('certification', e.target.value)
                        }
                        error={touched.certification && Boolean(errors.certification)}
                        helperText={touched.certification && errors.certification}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Start date of certification:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHAntdDateTimePicker
                        inputHeight={40}
                        data-testid="certificationStartDate"
                        format="MM/DD/YYYY"
                        sx={{ boxShadow: 'none', width: '100%' }}
                        allowClear={false}
                        value={
                          values['date_certification_start']
                            ? moment(values['date_certification_start'])
                            : null
                        }
                        onSelect={(value: Moment) =>
                          handleUpdateFieldValues(
                            'date_certification_start',
                            value.format('YYYY-MM-DD'),
                          )
                        }
                        onChange={(value: Moment) =>
                          handleUpdateFieldValues(
                            'date_certification_start',
                            value.format('YYYY-MM-DD'),
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>End date of certification:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHAntdDateTimePicker
                        inputHeight={40}
                        data-testid="certificationEndDate"
                        format="MM/DD/YYYY"
                        sx={{ boxShadow: 'none', width: '100%' }}
                        allowClear={false}
                        value={
                          values['date_certification_end']
                            ? moment(values['date_certification_end'])
                            : null
                        }
                        onSelect={(value: Moment) =>
                          handleUpdateFieldValues(
                            'date_certification_end',
                            value.format('YYYY-MM-DD'),
                          )
                        }
                        onChange={(value: Moment) =>
                          handleUpdateFieldValues(
                            'date_certification_end',
                            value.format('YYYY-MM-DD'),
                          )
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Employment Type:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHSelect
                        data-testid="employment_type"
                        inputHeight={40}
                        backgroundType={BackgroundTypes.White}
                        fullWidth
                        clearable
                        options={EmploymentTypeOptions.map((employmentType: IOption) => ({
                          value: employmentType.label,
                          label: employmentType.label,
                        }))}
                        optionDataTestIdPrefix="employment_type"
                        value={values['employment_type']}
                        onUpdate={(value: SelectValueType) => {
                          handleUpdateFieldValues('employment_type', value);
                        }}
                        error={touched.type && Boolean(errors.type)}
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Job title:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHSelect
                        data-testid="job_title"
                        inputHeight={40}
                        backgroundType={BackgroundTypes.White}
                        fullWidth
                        clearable
                        options={JobTitleOptions.map((JobTitle: IOption) => ({
                          value: JobTitle.label,
                          label: JobTitle.label,
                        }))}
                        optionDataTestIdPrefix="job_title"
                        value={values['job_title']}
                        onUpdate={(value: SelectValueType) => {
                          handleUpdateFieldValues('job_title', value);
                        }}
                        error={touched.type && Boolean(errors.type)}
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>State License:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerStateLicense',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['state_license']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('state_license', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Caqh Id:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'caqhId',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['caqh_id']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('caqh_id', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>

                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Payroll Id:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['payroll_id']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('payroll_id', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Email<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerEmail',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['email']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('email', e.target.value)
                        }
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Phone<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerPhone',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['phone']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('phone', e.target.value)
                        }
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        DOB<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHAntdDateTimePicker
                        inputHeight={40}
                        data-testid="providerDOB"
                        format="MM/DD/YYYY"
                        sx={{ boxShadow: 'none', width: '100%' }}
                        disabledDate={disabledFutureDate}
                        error={touched.dob && Boolean(errors.dob)}
                        helperText={touched.dob && required}
                        allowClear={false}
                        value={values['dob'] ? moment(values['dob']) : null}
                        onSelect={(value: Moment) =>
                          handleUpdateFieldValues('dob', value.format('YYYY-MM-DD'))
                        }
                        onChange={(value: Moment) =>
                          handleUpdateFieldValues('dob', value.format('YYYY-MM-DD'))
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Gender<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <RadioGroup
                        data-testid="providerGender"
                        row
                        value={values['sex']}
                        name="gender-radio-buttons-group"
                        onChange={(e: SelectChangeEvent<unknown>) =>
                          handleUpdateFieldValues('sex', e.target.value)
                        }
                      >
                        <FormControlLabel
                          data-testid="providerGenderMale"
                          value={'male'}
                          control={<Radio />}
                          label="Male"
                        />
                        <FormControlLabel value={'female'} control={<Radio />} label="Female" />
                      </RadioGroup>
                      {touched.sex && Boolean(errors.sex) && (
                        <FormHelperText sx={{ color: FORTA_ERROR, fontSize: '0.75rem' }}>
                          {errors.sex}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Street Address 1<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerStreetAddress1',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['street_address_line_one']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_line_one', e.target.value)
                        }
                        error={
                          touched.street_address_line_one && Boolean(errors.street_address_line_one)
                        }
                        helperText={
                          touched.street_address_line_one &&
                          errors.street_address_line_one
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>Street Address 2:</Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerStreetAddress2',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['street_address_line_two']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_line_two', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        City<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerCity',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['street_address_city']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_city', e.target.value)
                        }
                        error={touched.street_address_city && Boolean(errors.street_address_city)}
                        helperText={
                          touched.street_address_city && errors.street_address_city
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        State<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerState',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['street_address_state']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_state', e.target.value)
                        }
                        error={touched.street_address_state && Boolean(errors.street_address_state)}
                        helperText={
                          touched.street_address_state && errors.street_address_state
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={3.5}>
                      <Typography textAlign={'left'}>
                        Zip<span style={{ color: 'red' }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <FHInputField
                        inputHeight={40}
                        inputProps={
                          {
                            'data-testid': 'providerZip',
                          }
                        }
                        sx={{ boxShadow: 'none' }}
                        fullWidth
                        value={values['street_address_zip']}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleUpdateFieldValues('street_address_zip', e.target.value)
                        }
                        error={touched.street_address_zip && Boolean(errors.street_address_zip)}
                        helperText={
                          touched.street_address_zip && errors.street_address_zip
                        }
                      />
                    </Grid>
                    <Typography
                      textAlign={'left'}
                      mt={2}
                      ml={0.5}
                      color={FORTA_BROWN}
                      fontWeight={700}
                    >
                      Multi-language support is currently available only for guardians.
                    </Typography>
                    <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                      <Grid item xs={3.5}>
                        <Typography textAlign={'left'}>Supported Languages</Typography>
                      </Grid>
                      <Grid item xs={8.5}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {Object.values(SupportedLanguages).map((language) => {
                            const languageLabel =
                              language === SupportedLanguages.English ? 'English' : 'Spanish';
                            const isChecked =
                              values.supported_languages?.includes(language) || false;
                            return (
                              <FCheckbox
                                key={language}
                                label={languageLabel}
                                checked={isChecked}
                                sx={{
                                  '& .Mui-disabled': {
                                    opacity: 0.8,
                                    cursor: 'not-allowed',
                                  },
                                }}
                                disabled={language === SupportedLanguages.English}
                                onChange={(e) =>
                                  handleSupportedLanguageChange(language, e.target.checked)
                                }
                                data-testid={`supported-language-${language}`}
                              />
                            );
                          })}
                        </Box>
                      </Grid>
                    </Grid>
                    {values.supported_languages && values.supported_languages.length > 1 && (
                      <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                        <Grid item xs={3.5}>
                          <Typography textAlign={'left'}>Preferred Language</Typography>
                        </Grid>
                        <Grid item xs={8.5}>
                          <RadioGroup
                            data-testid="providerPreferredLanguage"
                            row
                            value={values['preferred_language']}
                            name="preferred-language-radio-buttons-group"
                            onChange={(e: SelectChangeEvent<unknown>) =>
                              handleUpdateFieldValues('preferred_language', e.target.value)
                            }
                          >
                            <FormControlLabel
                              data-testid="providerPreferredLanguageEnglish"
                              value={SupportedLanguages.English}
                              control={<Radio />}
                              label="English"
                            />
                            <FormControlLabel
                              value={SupportedLanguages.Spanish}
                              control={<Radio />}
                              label="Spanish"
                            />
                          </RadioGroup>
                        </Grid>
                      </Grid>
                    )}
                    {/* Traning status */}
                    <Typography
                      textAlign={'left'}
                      mt={2}
                      ml={0.5}
                      color={FORTA_BROWN}
                      fontWeight={700}
                    >
                      Training status applies only to BCBAs and vBTs and is ignored for all other
                      roles.
                    </Typography>
                    <Grid container p={0.5} spacing={1} direction="row" alignItems="center">
                      <Grid item xs={3.5}>
                        <Typography textAlign={'left'}>Training Status:</Typography>
                      </Grid>
                      <Grid item xs={8.5}>
                        <FHSelect
                          data-testid="status"
                          inputHeight={40}
                          backgroundType={BackgroundTypes.White}
                          fullWidth
                          clearable
                          options={ProviderTrainingStatusOptions.filter(
                            (employmentType: IOption): employmentType is IOption & { id: SelectValueType } =>
                              employmentType.id !== undefined,
                          ).map((employmentType) => ({
                            value: employmentType.id,
                            label: employmentType.label,
                          }))}
                          optionDataTestIdPrefix="status"
                          value={values['status']}
                          onUpdate={(value: SelectValueType) => {
                            handleUpdateFieldValues('status', value);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>

                <Box ml={0.5} mt={3} sx={{ display: 'flex' }}>
                  <FHButton
                    data-testid="providerSubmit"
                    onClick={submitForm}
                    disabled={formik.isSubmitting}
                  >
                    Submit
                  </FHButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default AddProviderForm;
